import { ReactElement, useContext, useEffect, useState } from "react";
import useApiState, { api } from "../../../hooks/use-api-state";
import Toggle from 'react-toggle'
import "react-toggle/style.css"
import { ICohortData } from "../../../messages/IGetCohortByIdResponse";
import {
  ICohortFeedback,
  IGetCohortFeedbacksResponse,
} from "../../../messages/IGetCohortFeedbacksResponse";
import NotificationsContext from "../../../store/notifications-context";
import Loading from "../../common/Loading";
import styles from "./CohortFeedbacks.module.css";

export default function CohortFeedbacks(props: {
  cohort: ICohortData;
}): ReactElement {
  const notificationsContext = useContext(NotificationsContext);
  const [apiState, setApiState] = useApiState(false);
  const [cohortFeedbacks, setCohortFeedbacks] = useState<IGetCohortFeedbacksResponse | null>(null);
  const [final, setFinal] = useState<boolean>((Math.floor(Date.now() / 1000))>=props.cohort.enddate_secs_utc);

  useEffect(() => {
    api.getCohortFeedbacks(setApiState, props.cohort.id, final)
      .then((feedbacks: IGetCohortFeedbacksResponse) => {
        setCohortFeedbacks(feedbacks);
      });
  }, [final, props.cohort.id, setApiState]);

  function copyFeedbackData() {
    let feedbackString = "";
    if (cohortFeedbacks !== null) {
      for (const feedback of cohortFeedbacks.feedbacks) {
        feedbackString += `${props.cohort.code}\t${feedback.name}\t${feedback.email}\t${feedback.timestr}\t`;
        feedbackString += `${feedback.relevance??""}\t${feedback.objectives??""}\t${feedback.pace??""}\t${feedback.duration??""}\t${feedback.material??""}\t`;
        feedbackString += `${feedback.labs??""}\t${feedback.instructor??""}\t${feedback.presentation??""}\t${feedback.overall??""}\t${feedback.promote??""}\t`;
        feedbackString += `${!!feedback.like ? feedback.like.replace(/(\r\n|\n|\r)/gm, "") : ""}\t`;
        feedbackString += `${!!feedback.improve ? feedback.improve.replace(/(\r\n|\n|\r)/gm, "") : ""}\t`;
        feedbackString += `${!!feedback.action1 ? feedback.action1.replace(/(\r\n|\n|\r)/gm, "") : ""}\t`;
        feedbackString += `${!!feedback.action2 ? feedback.action2.replace(/(\r\n|\n|\r)/gm, "") : ""}\t`;
        feedbackString += `${!!feedback.action3 ? feedback.action3.replace(/(\r\n|\n|\r)/gm, "") : ""}\t`;
        feedbackString += `\n`;
      }
    }

    if (feedbackString.length !== 0) {
      navigator.clipboard.writeText(feedbackString);
      notificationsContext.addNotification({
        message: "Feedback data copied to the clipboard",
      });
    } else {
      notificationsContext.addNotification({
        message: "There is no data to be copied to the clipboard",
      });
    }
  }

  function getNameField(email: string, name: string): string {
    if (!name) {
      return email;
    } else if (!email) {
      return name;
    }
    return `${email} (${name})`
  }

  return (
    <>
      <Loading isLoading={apiState.isLoading}>
        <div className={styles.feedback_table_header}>
          <div className={styles.midterm_toggle_div}>
            <Toggle
              checked={final}
              icons={false}
              onChange={(e:React.ChangeEvent<HTMLInputElement>) => {
                setFinal(e.target.checked);
              }} />
              <span>{final ? "Final" : "Midterm"}</span>
          </div>
          <div className={styles.stat_div}>
            <b>
              Received{" "}
              {cohortFeedbacks === null || cohortFeedbacks.feedbacks.length}{" "}
              forms out of {props.cohort.membercount}
            </b>
          </div>
          <button
            className="btn primary narrow"
            onClick={copyFeedbackData}
            disabled={
              cohortFeedbacks === null || cohortFeedbacks.feedbacks.length === 0
            }
          >
            Put feedback data to clipboard
          </button>
        </div>
        <div className="cy__form form__evaluations">
          <div className={styles.feedbacks_container}>
            <table className={`styled-table ${styles.feedback_table}`}>
              <thead>
                <tr>
                  <th
                    className={`${styles.table_textName} ${styles.table_text_header}`}
                  >
                    Email and name
                  </th>
                  <th
                    className={`${styles.table_consent_icons} ${styles.table_text_header}`}
                    style={{ fontSize: "0.8em" }}
                  >
                    Consent
                  </th>
                  <th className={styles.table_num_header}>Relevance</th>
                  <th className={styles.table_num_header}>Expectations</th>
                  <th className={styles.table_num_header}>Pace</th>
                  <th className={styles.table_num_header}>Duration</th>
                  <th className={styles.table_num_header}>Material quality</th>
                  <th className={styles.table_num_header}>Labs</th>
                  <th className={styles.table_num_header}>
                    Instructor knowledge
                  </th>
                  <th className={styles.table_num_header}>
                    Instructor presentation
                  </th>
                  <th className={styles.table_num_header}>Overall feeling</th>
                  <th className={styles.table_num_header}>
                    Recommend to others
                  </th>
                  <th
                    className={`${styles.table_text6} ${styles.table_text_header}`}
                  >
                    Positive
                  </th>
                  <th
                    className={`${styles.table_text6} ${styles.table_text_header}`}
                  >
                    Improve
                  </th>
                  <th
                    className={`${styles.table_text4} ${styles.table_text_header}`}
                  >
                    Action for tomorrow
                  </th>
                  <th
                    className={`${styles.table_text4} ${styles.table_text_header}`}
                  >
                    Action for tomorrow
                  </th>
                  <th
                    className={`${styles.table_text4} ${styles.table_text_header}`}
                  >
                    Action for tomorrow
                  </th>
                </tr>
              </thead>
              <tbody>
                {cohortFeedbacks?.feedbacks.map(
                  (feedback: ICohortFeedback, index) => {
                    return (
                      <tr key={index}>
                        <td className={`${styles.table_textName} ${feedback.user_id ? styles.hasUserId : ""}`}
                            title={feedback.user_id ? `Authenticated feedback (user was logged in)` : ""}
                        >{getNameField(feedback.email,feedback.name)}</td>
                        <td className={styles.table_consent_icons}>
                          {feedback.usefeedback_name && (
                            <img
                              src="/icons/user_40x40_2.png"
                              className={`${styles.consent_icon}`}
                              title={"User gave consent to cite the review with his/her name."}
                            />
                          )}
                          {feedback.usefeedback_company && (
                            <img
                              src="/icons/banking_finance_40x40.png"
                              className={`${styles.consent_icon}`}
                              title={"User gave consent to cite the review with the company name."}
                            />
                          )}
                        </td>
                        <td className={styles.table_num}>
                          {feedback.relevance}
                        </td>
                        <td className={styles.table_num}>
                          {feedback.objectives}
                        </td>
                        <td className={styles.table_num}>{feedback.pace}</td>
                        <td className={styles.table_num}>
                          {feedback.duration}
                        </td>
                        <td className={styles.table_num}>
                          {feedback.material}
                        </td>
                        <td className={styles.table_num}>{feedback.labs}</td>
                        <td className={styles.table_num}>
                          {feedback.instructor}
                        </td>
                        <td className={styles.table_num}>
                          {feedback.presentation}
                        </td>
                        <td className={styles.table_num}>{feedback.overall}</td>
                        <td className={styles.table_num}>{feedback.promote}</td>
                        <td className={styles.table_text6}>{feedback.like}</td>
                        <td className={styles.table_text6}>
                          {feedback.improve}
                        </td>
                        <td className={styles.table_text4}>
                          {feedback.action1}
                        </td>
                        <td className={styles.table_text4}>
                          {feedback.action2}
                        </td>
                        <td className={styles.table_text4}>
                          {feedback.action3}
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Loading>
    </>
  );
}
